import { Phone, Mail, MapPin, Facebook, Instagram, Twitter, Linkedin } from "lucide-react";
import Newsletter from "@/components/Newsletter";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_0808c772-0b84-4caf-af02-d4cf76d8b0d8/artifacts/49nqn57a_CloudJet_Clean_Transparent.png";

const quickLinks = [
  { name: "Home", href: "#home" },
  { name: "About Us", href: "#about" },
  { name: "Services", href: "#services" },
  { name: "Destinations", href: "#destinations" },
  { name: "Contact", href: "#contact" }
];

const services = [
  "Holiday Packages",
  "Flight Bookings",
  "Hotel Reservations",
  "Custom Itineraries",
  "Group Travel",
  "Corporate Travel"
];

const socialLinks = [
  { icon: Facebook, href: "#", label: "Facebook" },
  { icon: Instagram, href: "#", label: "Instagram" },
  { icon: Twitter, href: "#", label: "Twitter" },
  { icon: Linkedin, href: "#", label: "LinkedIn" }
];

export default function Footer() {
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <footer className="bg-sky-900 text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-8">
        {/* Newsletter Section */}
        <div className="mb-12">
          <Newsletter />
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <img 
              src={LOGO_URL} 
              alt="CloudJet" 
              className="h-16 w-auto mb-4 brightness-0 invert"
              data-testid="footer-logo"
            />
            <p className="text-sky-200 mb-6">
              Making travel dreams come true since 2010. Your trusted partner for 
              unforgettable adventures across the globe.
            </p>
            <div className="flex gap-4">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  className="w-10 h-10 bg-sky-800 rounded-lg flex items-center justify-center hover:bg-sky-700 transition-colors"
                  aria-label={social.label}
                  data-testid={`footer-social-${social.label.toLowerCase()}`}
                >
                  <social.icon size={20} />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-4">Quick Links</h3>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    className="text-sky-200 hover:text-white transition-colors"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(' ', '-')}`}
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-bold mb-4">Our Services</h3>
            <ul className="space-y-3">
              {services.map((service, index) => (
                <li key={index}>
                  <span className="text-sky-200">{service}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-bold mb-4">Contact Us</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin size={20} className="text-sky-400 flex-shrink-0 mt-1" />
                <span className="text-sky-200">
                  Sydney, NSW<br />Australia
                </span>
              </li>
              <li>
                <a 
                  href="tel:+61295387640" 
                  className="flex items-center gap-3 text-sky-200 hover:text-white transition-colors"
                  data-testid="footer-phone"
                >
                  <Phone size={20} className="text-sky-400" />
                  +61 2 9538 7640
                </a>
              </li>
              <li>
                <a 
                  href="mailto:hello@cloudjet.com.au" 
                  className="flex items-center gap-3 text-sky-200 hover:text-white transition-colors"
                  data-testid="footer-email"
                >
                  <Mail size={20} className="text-sky-400" />
                  hello@cloudjet.com.au
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-sky-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sky-300 text-sm">
              © {new Date().getFullYear()} CloudJet. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm">
              <a href="#" className="text-sky-300 hover:text-white transition-colors">Privacy Policy</a>
              <a href="#" className="text-sky-300 hover:text-white transition-colors">Terms of Service</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
